import React, {useEffect, useState} from 'react';
import {Button, Col, Collapse, Descriptions, Flex, Input, message, Modal, Row, Space, Tag, Typography} from 'antd';
import {ArrowLeftOutlined, DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import PdfLink from '../../components/PdfLink';
import {useNavigate, useParams} from 'react-router-dom';
import ENDPOINTS from '../../constants/endpoints';
import {formatPath, ROUTES} from '../../constants/PathRoutes';
import useDefaultRequest from '../../hooks/useDefaultRequest';
import Loading from '../../components/admin/Loading';
import {BuildFieldErrorsDict, dateFormat} from '../../utils/utils';
import {PRESCRIPTION_STATUS_COLOR} from '../../utils/colors';
import api from '../../services/api';
import CustomButton from '../../components/data_entry/Button';
import Form from '../../components/data_entry/form/Form';
import InputFormField from '../../components/data_entry/form/InputFormField';
import InputNumberFormField from '../../components/data_entry/form/InputNumberFormField';
import SelectFormField from '../../components/data_entry/form/SelectFormField';
import TextAreaFormField from '../../components/data_entry/form/TextAreaFormField';
import DatePickerFormField from '../../components/data_entry/form/DatePickerFormField';

const FORM_NAMES = {
    DISCOUNT_VALUE: 'discount_value',
    EXPIRATION_DATE: 'expiration_date',
    DOCTOR_CRM: 'doctor_crm',
    DOCTOR_CRMV: 'doctor_crmv',
    NOTES: 'notes',
    ITEMS: 'items',
    ITEM_PRODUCT: 'product',
    ITEM_QUANTITY: 'quantity',
};

const PRODUCT_REQUEST = {
    url: ENDPOINTS.ECOMMERCE_PRODUCT_LIST,
    params: {requires_prescription: true},
};


const GENDER_LABEL = {
    MALE: 'Masculino',
    FEMALE: 'Feminino',
};

const KIND_LABEL = {
    PATIENT: 'Paciente',
    CAREGIVER: 'Cuidador',
};

export default function AdminPrescriptionDetail() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [invalidateModal, setInvalidateModal] = useState(false);
    const [invalidateNotes, setInvalidateNotes] = useState('');
    const [invalidateLoading, setInvalidateLoading] = useState(false);

    const {data: prescription, isLoading: loading} = useDefaultRequest({
        url: formatPath(ENDPOINTS.ECOMMERCE_STAFF_PRESCRIPTION_DETAIL, {id}),
    });


    useEffect(() => {
        if (!prescription) return;
        form.setFieldsValue({
            [FORM_NAMES.DISCOUNT_VALUE]: prescription.discount_value,
            [FORM_NAMES.EXPIRATION_DATE]: dateFormat(prescription.expiration_date),
            [FORM_NAMES.DOCTOR_CRM]: prescription.doctor_crm,
            [FORM_NAMES.DOCTOR_CRMV]: prescription.doctor_crmv,
            [FORM_NAMES.NOTES]: prescription.notes,
        });
    }, [prescription, form]);

    const onFinish = async (values) => {
        setSubmitLoading(true);
        try {
            const payload = {
                ...values,
                [FORM_NAMES.EXPIRATION_DATE]: values[FORM_NAMES.EXPIRATION_DATE]?.format
                    ? values[FORM_NAMES.EXPIRATION_DATE].format('YYYY-MM-DD')
                    : values[FORM_NAMES.EXPIRATION_DATE],
            };
            await api.post(
                formatPath(ENDPOINTS.ECOMMERCE_STAFF_PRESCRIPTION_VALIDATE, {id}),
                payload
            );
            message.success('Prescrição validada com sucesso');
            navigate(ROUTES.ADMIN_ECOMMERCE_PRESCRIPTION_LIST);
        } catch (error) {
            BuildFieldErrorsDict(error, form.setFields);
        } finally {
            setSubmitLoading(false);
        }
    };

    const onInvalidate = async () => {
        setInvalidateLoading(true);
        try {
            await api.post(
                formatPath(ENDPOINTS.ECOMMERCE_STAFF_PRESCRIPTION_INVALIDATE, {id}),
                {notes: invalidateNotes}
            );
            message.success('Prescrição invalidada com sucesso');
            navigate(ROUTES.ADMIN_ECOMMERCE_PRESCRIPTION_LIST);
        } catch (error) {
            BuildFieldErrorsDict(error, null);
        } finally {
            setInvalidateLoading(false);
        }
    };

    if (loading) return <Loading/>;

    const dependentProfile = prescription?.dependent_profile;

    const dependentCollapseItems = dependentProfile ? [
        {
            key: 'dependent',
            label: 'Perfil do Paciente',
            children: (
                <Descriptions bordered column={{xs: 1, md: 2}} size="middle" layout={'vertical'}>
                    <Descriptions.Item label="Nome">
                        {`${dependentProfile.first_name} ${dependentProfile.last_name}`}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tipo">
                        {KIND_LABEL[dependentProfile.kind] ?? dependentProfile.kind ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Gênero">
                        {GENDER_LABEL[dependentProfile.gender] ?? dependentProfile.gender ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="CPF">
                        {dependentProfile.cpf || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="RG">
                        {dependentProfile.rg || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data de Nascimento">
                        {dependentProfile.birth_date
                            ? dateFormat(dependentProfile.birth_date)
                            : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Condição Médica" span={2}>
                        {dependentProfile.medical_condition || '-'}
                    </Descriptions.Item>
                    {dependentProfile.medical_report_pdf && (
                        <Descriptions.Item label="Laudo Médico" span={2}>
                            <PdfLink url={dependentProfile.medical_report_pdf} label="Ver Laudo PDF"/>
                        </Descriptions.Item>
                    )}
                </Descriptions>
            ),
        },
    ] : [];

    return (
        <Row gutter={[16, 24]}>
            <Col xs={24}>
                <Space align="center">
                    <Button
                        icon={<ArrowLeftOutlined/>}
                        onClick={() => navigate(ROUTES.ADMIN_ECOMMERCE_PRESCRIPTION_LIST)}
                    >
                        Voltar
                    </Button>
                    <Typography.Title level={4} style={{margin: 0}}>
                        Prescrição{' '}
                        <Tag color={PRESCRIPTION_STATUS_COLOR[prescription?.status] ?? 'default'}>
                            {prescription?.status_display}
                        </Tag>
                    </Typography.Title>
                </Space>
            </Col>

            {dependentCollapseItems.length > 0 && (
                <Col xs={24}>
                    <Collapse
                        defaultActiveKey={['dependent']}
                        items={dependentCollapseItems}
                    />
                </Col>
            )}

            <Col xs={24}>
                <Form form={form} onFinish={onFinish}>
                    <Row gutter={[16, 16]}>
                        {prescription?.prescription_file && (
                            <Col xs={24}>
                                <Form.Item label="PDF da Prescrição">
                                    <PdfLink url={prescription.prescription_file}/>
                                </Form.Item>
                            </Col>
                        )}
                        <Col xs={24}>
                            <Form.List
                                name={FORM_NAMES.ITEMS}
                                initialValue={[{}]}
                            >
                                {(fields, {add, remove}) => (
                                    <Row gutter={[16, 0]}>
                                        {fields.map(({key, name, ...restField}) => (
                                            <React.Fragment key={key}>
                                                <Col xs={24} lg={12}>
                                                    <SelectFormField
                                                        request={PRODUCT_REQUEST}
                                                        form={{
                                                            ...restField,
                                                            label: 'Produto',
                                                            name: [name, FORM_NAMES.ITEM_PRODUCT],
                                                            rules: [
                                                                {required: true},
                                                                ({getFieldValue}) => ({
                                                                    validator(_, value) {
                                                                        if (!value) return Promise.resolve();
                                                                        const items = getFieldValue(FORM_NAMES.ITEMS) || [];
                                                                        const duplicates = items.filter(
                                                                            (item, index) => item?.product === value && index !== name
                                                                        );
                                                                        if (duplicates.length > 0) {
                                                                            return Promise.reject('Produto já adicionado');
                                                                        }
                                                                        return Promise.resolve();
                                                                    },
                                                                }),
                                                            ],
                                                        }}
                                                    />
                                                </Col>
                                                <Col xs={24} lg={10}>
                                                    <InputNumberFormField
                                                        min={1}
                                                        form={{
                                                            ...restField,
                                                            label: 'Quantidade',
                                                            name: [name, FORM_NAMES.ITEM_QUANTITY],
                                                            rules: [{required: true}],
                                                        }}
                                                    />
                                                </Col>
                                                <Col xs={24} lg={2} style={{display: 'flex', alignItems: 'center', paddingTop: 6}}>
                                                    {fields.length > 1 && (
                                                        <Button
                                                            danger
                                                            icon={<DeleteOutlined/>}
                                                            onClick={() => remove(name)}
                                                        />
                                                    )}
                                                </Col>
                                            </React.Fragment>
                                        ))}
                                        <Col xs={24}>
                                            <Flex justify={'end'}>
                                                <Button
                                                    icon={<PlusOutlined/>}
                                                    onClick={() => add()}
                                                >
                                                    Adicionar Item
                                                </Button>
                                            </Flex>

                                        </Col>
                                    </Row>
                                )}
                            </Form.List>
                        </Col>
                        <Col xs={24} md={6}>
                            <InputNumberFormField
                                min={0}
                                max={100}
                                addonAfter="%"
                                form={{
                                    label: 'Desconto',
                                    name: FORM_NAMES.DISCOUNT_VALUE,
                                    rules: [{required: true}],
                                }}
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <DatePickerFormField
                                form={{
                                    label: 'Data de Validade',
                                    name: FORM_NAMES.EXPIRATION_DATE,
                                    rules: [{required: true}],
                                }}
                            />
                        </Col>
                        {dependentProfile?.kind === 'PATIENT' && (
                            <Col xs={24} md={6}>
                                <InputFormField
                                    form={{
                                        label: 'CRM',
                                        name: FORM_NAMES.DOCTOR_CRM,
                                        rules: [{required: true}],
                                    }}
                                />
                            </Col>
                        )}
                        {dependentProfile?.kind === 'PET' && (
                            <Col xs={24} md={6}>
                                <InputFormField
                                    form={{
                                        label: 'CRMV',
                                        name: FORM_NAMES.DOCTOR_CRMV,
                                        rules: [{required: true}],
                                    }}
                                />
                            </Col>
                        )}
                        <Col xs={24}>
                            <TextAreaFormField
                                rows={3}
                                form={{
                                    label: 'Observações',
                                    name: FORM_NAMES.NOTES,
                                }}
                            />
                        </Col>
                        <Col xs={24}>
                            <Flex justify="end" gap={8}>
                                <Button
                                    danger
                                    onClick={() => setInvalidateModal(true)}
                                >
                                    Invalidar Prescrição
                                </Button>
                                <CustomButton htmlType="submit" loading={submitLoading}>
                                    Validar Prescrição
                                </CustomButton>
                            </Flex>
                        </Col>
                    </Row>
                </Form>
            </Col>

            <Modal
                title="Invalidar Prescrição"
                open={invalidateModal}
                onCancel={() => setInvalidateModal(false)}
                onOk={onInvalidate}
                okText="Confirmar Invalidação"
                cancelText="Cancelar"
                okButtonProps={{danger: true, loading: invalidateLoading}}
            >
                <Typography.Paragraph>
                    Tem certeza que deseja invalidar esta prescrição? Informe o motivo abaixo.
                </Typography.Paragraph>
                <Input.TextArea
                    rows={4}
                    placeholder="Motivo da invalidação..."
                    value={invalidateNotes}
                    onChange={(e) => setInvalidateNotes(e.target.value)}
                />
            </Modal>

        </Row>
    );
}